import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, Mail, Globe, Lock, Users, CreditCard, Database, Bell, Baby, FileText } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <div className="bg-black/30 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-purple-400" />
            <div>
              <h1 className="text-2xl font-bold text-white">Privacy Policy</h1>
              <p className="text-purple-300 text-sm">FoxSeller</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Last Updated */}
        <div className="text-center">
          <span className="inline-block bg-purple-500/20 text-purple-300 px-4 py-2 rounded-full text-sm border border-purple-500/30">
            Last updated: March 2026
          </span>
        </div>

        {/* Introduction */}
        <Card className="bg-white/5 backdrop-blur-sm border-white/10">
          <CardContent className="p-6">
            <p className="text-gray-300 leading-relaxed">
              FoxSeller ("we", "our", or "us") respects your privacy and is committed to protecting your personal data. 
              This Privacy Policy explains how we collect, use, and protect your information when you use our mobile application.
            </p>
          </CardContent>
        </Card>

        {/* Section 1: Information We Collect */}
        <Card className="bg-white/5 backdrop-blur-sm border-white/10">
          <CardHeader className="border-b border-white/10">
            <CardTitle className="flex items-center gap-3 text-white">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Database className="w-5 h-5 text-blue-400" />
              </div>
              1. Information We Collect
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div>
              <h3 className="text-purple-300 font-semibold mb-3 flex items-center gap-2">
                <span className="text-lg">📌</span> Personal Information
              </h3>
              <ul className="space-y-2 text-gray-300 ml-6">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-purple-400 rounded-full"></span>
                  Name
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-purple-400 rounded-full"></span>
                  Email address
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-purple-400 rounded-full"></span>
                  User ID (from supported platforms)
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-purple-400 rounded-full"></span>
                  Payment-related information (processed by third-party providers)
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-purple-300 font-semibold mb-3 flex items-center gap-2">
                <span className="text-lg">📱</span> Usage Data
              </h3>
              <ul className="space-y-2 text-gray-300 ml-6">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-purple-400 rounded-full"></span>
                  Device information (model, OS, IP address)
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-purple-400 rounded-full"></span>
                  App usage behavior
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-purple-400 rounded-full"></span>
                  Transaction history
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: How We Use Your Information */}
        <Card className="bg-white/5 backdrop-blur-sm border-white/10">
          <CardHeader className="border-b border-white/10">
            <CardTitle className="flex items-center gap-3 text-white">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <Users className="w-5 h-5 text-green-400" />
              </div>
              2. How We Use Your Information
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-gray-300 mb-4">We use your information to:</p>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-start gap-3">
                <span className="text-green-400 mt-1">✓</span>
                Process purchases and transactions
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-400 mt-1">✓</span>
                Provide and improve our services
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-400 mt-1">✓</span>
                Offer customer support
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-400 mt-1">✓</span>
                Send updates, promotions, and important notifications
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-400 mt-1">✓</span>
                Prevent fraud and ensure platform security
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Section 3: Payments and Third Parties */}
        <Card className="bg-white/5 backdrop-blur-sm border-white/10">
          <CardHeader className="border-b border-white/10">
            <CardTitle className="flex items-center gap-3 text-white">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <CreditCard className="w-5 h-5 text-yellow-400" />
              </div>
              3. Payments and Third Parties
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <p className="text-gray-300 leading-relaxed">
              All payments are securely processed by trusted third-party providers (such as payment gateways and financial platforms). 
              We do not store full payment details such as credit card numbers.
            </p>
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
              <p className="text-yellow-200 text-sm">
                <strong>Note:</strong> Third-party services may collect and process data according to their own privacy policies.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Section 4: Data Sharing */}
        <Card className="bg-white/5 backdrop-blur-sm border-white/10">
          <CardHeader className="border-b border-white/10">
            <CardTitle className="flex items-center gap-3 text-white">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <Shield className="w-5 h-5 text-red-400" />
              </div>
              4. Data Sharing
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
              <p className="text-green-300 font-semibold">
                We do not sell your personal data.
              </p>
            </div>
            <p className="text-gray-300">We may share your information only when necessary:</p>
            <ul className="space-y-2 text-gray-300 ml-4">
              <li className="flex items-start gap-3">
                <span className="text-gray-400">•</span>
                With payment providers to complete transactions
              </li>
              <li className="flex items-start gap-3">
                <span className="text-gray-400">•</span>
                With service providers that help operate the app
              </li>
              <li className="flex items-start gap-3">
                <span className="text-gray-400">•</span>
                When required by law or legal obligations
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Section 5: Data Security */}
        <Card className="bg-white/5 backdrop-blur-sm border-white/10">
          <CardHeader className="border-b border-white/10">
            <CardTitle className="flex items-center gap-3 text-white">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <Lock className="w-5 h-5 text-purple-400" />
              </div>
              5. Data Security
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <p className="text-gray-300 leading-relaxed">
              We implement appropriate security measures to protect your data from unauthorized access, alteration, or disclosure.
            </p>
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
              <p className="text-orange-200 text-sm">
                However, no system is 100% secure, and we encourage users to protect their own account credentials.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Section 6: Data Retention */}
        <Card className="bg-white/5 backdrop-blur-sm border-white/10">
          <CardHeader className="border-b border-white/10">
            <CardTitle className="flex items-center gap-3 text-white">
              <div className="p-2 bg-cyan-500/20 rounded-lg">
                <Database className="w-5 h-5 text-cyan-400" />
              </div>
              6. Data Retention
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-gray-300 leading-relaxed">
              We retain your information only as long as necessary to provide our services and comply with legal obligations.
            </p>
          </CardContent>
        </Card>

        {/* Section 7: Your Rights */}
        <Card className="bg-white/5 backdrop-blur-sm border-white/10">
          <CardHeader className="border-b border-white/10">
            <CardTitle className="flex items-center gap-3 text-white">
              <div className="p-2 bg-indigo-500/20 rounded-lg">
                <Users className="w-5 h-5 text-indigo-400" />
              </div>
              7. Your Rights
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <p className="text-gray-300">Depending on your location, you may have the right to:</p>
            <ul className="space-y-2 text-gray-300 ml-4">
              <li className="flex items-start gap-3">
                <span className="text-indigo-400">→</span>
                Access your personal data
              </li>
              <li className="flex items-start gap-3">
                <span className="text-indigo-400">→</span>
                Request correction or deletion
              </li>
              <li className="flex items-start gap-3">
                <span className="text-indigo-400">→</span>
                Withdraw consent for data usage
              </li>
            </ul>
            <p className="text-gray-400 text-sm mt-4">
              To make a request, contact us using the information below.
            </p>
          </CardContent>
        </Card>

        {/* Section 8: Children's Privacy */}
        <Card className="bg-white/5 backdrop-blur-sm border-white/10">
          <CardHeader className="border-b border-white/10">
            <CardTitle className="flex items-center gap-3 text-white">
              <div className="p-2 bg-pink-500/20 rounded-lg">
                <Baby className="w-5 h-5 text-pink-400" />
              </div>
              8. Children's Privacy
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-gray-300 leading-relaxed">
              FoxSeller is not intended for users under the age of 13. We do not knowingly collect data from children.
            </p>
          </CardContent>
        </Card>

        {/* Section 9: Changes to This Policy */}
        <Card className="bg-white/5 backdrop-blur-sm border-white/10">
          <CardHeader className="border-b border-white/10">
            <CardTitle className="flex items-center gap-3 text-white">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <FileText className="w-5 h-5 text-amber-400" />
              </div>
              9. Changes to This Policy
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-gray-300 leading-relaxed">
              We may update this Privacy Policy from time to time. Updates will be posted within the app or on our official channels.
            </p>
          </CardContent>
        </Card>

        {/* Section 10: Contact Us */}
        <Card className="bg-white/5 backdrop-blur-sm border-white/10">
          <CardHeader className="border-b border-white/10">
            <CardTitle className="flex items-center gap-3 text-white">
              <div className="p-2 bg-teal-500/20 rounded-lg">
                <Mail className="w-5 h-5 text-teal-400" />
              </div>
              10. Contact Us
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-gray-300 mb-4">
              If you have any questions about this Privacy Policy, please contact us:
            </p>
            <div className="space-y-3">
              <a 
                href="mailto:suport@foxagency.live" 
                className="flex items-center gap-3 text-teal-400 hover:text-teal-300 transition-colors"
              >
                <Mail className="w-5 h-5" />
                <span>suport@foxagency.live</span>
              </a>
              <a 
                href="https://www.moedasonline.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-teal-400 hover:text-teal-300 transition-colors"
              >
                <Globe className="w-5 h-5" />
                <span>www.moedasonline.com</span>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center py-8 border-t border-white/10">
          <p className="text-gray-500 text-sm">
            © 2026 FoxSeller. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
